import React, { Component } from 'react';
import PropTypes from 'prop-types';
import lodashCompact from 'lodash/fp/compact';

import {
  SR_API_ACTION_UTILITY_VERIFY_ACCOUNT_EXIST,
  SR_API_ACTION_UTILITY_VERIFY_PHONE_SMS_CODE_REQUESTED,
} from 'api/utilityActionTypes';
import { SCREEN_ID_UTILITY_RESET_PASSWORD } from 'client/constants/screenIds';
import { UTILITY_USER_ROLE_ANONYMOUS } from 'common/utility/userRoles';

import log from 'client/lib/log';
import withScreenId from 'client/helpers/withScreenId';
import withTransitionHook from 'client/helpers/withTransitionHook';
import requestUtilityApi from 'client/actions/requestUtilityApi';
import withScrollToTop from 'client/helpers/withScrollToTop';
import { withUtilityApiState } from 'client/helpers/withApiState';
import allowedUtilityRoles from 'client/helpers/allowedUtilityRoles';

import branch from 'client/core/branch';
import UtilityAuthActions from 'client/actions/UtilityAuth';
import UtilityPartnerActions from 'client/actions/UtilityPartner';

import withLocationQuery from 'client/helpers/withLocationQuery';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import Navbar from 'client/components/Navbar';
import SrLink from 'client/components/SrLink/SrLink';
import SrPasswordResetSmsForm from 'client/components/SrPasswordResetSmsForm';
import SrSmsSendingIndicator from 'client/components/SrSmsSendingIndicator';
import SrErrorMessage from 'client/components/SrErrorMessage/SrErrorMessage';

@withTransitionHook
@allowedUtilityRoles({
  roles: [UTILITY_USER_ROLE_ANONYMOUS],
  redirectUrl: '/utility-setup/session',
})
@withUtilityApiState({
  verifyPhoneApiState: {
    action: SR_API_ACTION_UTILITY_VERIFY_PHONE_SMS_CODE_REQUESTED,
  },
})
@branch(({ match }) => {
  const cursors = {};

  if (match.params.partnerSlug) {
    cursors.utilityPartner = ['utilityPartners', 'bySlug', match.params.partnerSlug];
  }

  return cursors;
})
@withScreenId(SCREEN_ID_UTILITY_RESET_PASSWORD)
@withScrollToTop
@withLocationQuery
class UtilityResetPasswordPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Reset Password',
    section: SCREEN_ID_UTILITY_RESET_PASSWORD,
  });

  static fetchData(match, inject) {
    return Promise.all(
      lodashCompact([
        match.params.partnerSlug &&
          UtilityPartnerActions.getBySlug(inject, {
            slug: match.params.partnerSlug,
          }),
      ]),
    );
  }

  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    history: PropTypes.object.isRequired,
    verifyPhoneApiState: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      showSmsSending: false,
      phoneNumberDoesNotExist: false,
      accountPendingSignup: false,
      phone: '',
    };
    this._unmounting = false;
    this._onRequestVerificationCode = this._onRequestVerificationCode.bind(this);
  }

  componentWillUnmount() {
    this._unmounting = true;
  }

  async _onRequestVerificationCode({ phone }) {
    const { dispatch, history, location } = this.props;

    const urlParts = location.pathname.split('/');
    urlParts.pop();

    this.setState({
      showSmsSending: false,
      phoneNumberDoesNotExist: false,
      accountPendingSignup: false,
      phone,
    });

    try {
      const accountValidation = await dispatch(requestUtilityApi, {
        action: SR_API_ACTION_UTILITY_VERIFY_ACCOUNT_EXIST,
        payload: {
          phone: phone,
        },
      });

      if (accountValidation?.userExists && !accountValidation?.pendingSignup) {
        await dispatch(UtilityAuthActions.resetPasswordSmsCodeRequested, {
          phone: phone,
          isUtilityProfit: urlParts[1] === 'utility-profit',
        });

        this.setState({
          showSmsSending: true,
        });

        setTimeout(() => {
          if (!this._unmounting) {
            this.setState({
              showSmsSending: false,
            });
          }

          const urlParts = location.pathname.split('/');
          urlParts.pop();

          history.push(`${urlParts.join('/')}/reset-password-verify-code${location.search}`);
        }, 3000);
      } else {
        this.setState({
          showSmsSending: false,
          phoneNumberDoesNotExist: !accountValidation.userExists,
          accountPendingSignup: accountValidation.pendingSignup,
        });
      }
    } catch (error) {
      log.error(error);
    }
  }

  render() {
    const { verifyPhoneApiState, utilityPartner, location, locationQuery } = this.props;
    const { showSmsSending, phoneNumberDoesNotExist, accountPendingSignup, phone } = this.state;

    const urlParts = location.pathname.split('/');
    urlParts.pop();

    const isUtilityProfit = urlParts[1] === 'utility-profit';
    const isActivatingAccount = locationQuery.activate === 'true';
    const prefillPhone = locationQuery.phone;

    return (
      <div className="page-reset-password">
        <Navbar
          disableRedirect
          customLogo={
            <SrFlex justify="center">
              <SrBox
                backgroundSize="contain"
                backgroundPosition="center"
                backgroundRepeat="no-repeat"
                height="40px"
                width="100%"
                maxWidth="166px"
                backgroundImage={`url(${
                  isUtilityProfit ? '/images/utility-profit-logo.svg' : utilityPartner?.logoUrl
                })`}
              />
            </SrFlex>
          }
          leftButton={
            <SrLink
              className="link back"
              redirectToLastLocation
              restoreScrollPosition
              to={urlParts.join('/')}
            >
              Back
            </SrLink>
          }
        />
        <div className="resetContainer">
          <div className="resetFormContainer">
            {phoneNumberDoesNotExist && (
              <SrErrorMessage>Sorry, there is no account with that phone number</SrErrorMessage>
            )}
            {accountPendingSignup && (
              <SrErrorMessage>
                Please check your email for an account activation link
              </SrErrorMessage>
            )}
            {!verifyPhoneApiState.inProgress && !showSmsSending && (
              <SrPasswordResetSmsForm
                activatingAccount={isActivatingAccount}
                prefillPhone={prefillPhone}
                onSubmit={this._onRequestVerificationCode}
              />
            )}
            {showSmsSending && <SrSmsSendingIndicator phone={phone} />}
          </div>
        </div>
      </div>
    );
  }
}

UtilityResetPasswordPage.displayName = 'UtilityResetPasswordPage';

export default UtilityResetPasswordPage;
